// Installer-facing on-site installation checklist (sodistore home only).
// Distinct from the internal monitor-onboarding checklist (ChecklistTypes.tsx) —
// no assignee, no done-date; per-step substeps + comments + per-installation
// signature ("Bemerkungen" + typed-name e-signature).

export type OnSiteChecklistSubtask =
  | { kind: 'task'; text: string; checked: boolean }
  | { kind: 'heading'; heading: string };

export type OnSiteChecklistItem = {
  id: number;
  installationId: number;
  stepNumber: number;
  stepKey: string;
  checked: boolean;
  comments: string;
  subtasks: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OnSiteChecklistSignature = {
  id: number;
  installationId: number;
  remarks: string;
  signedByName: string | null;
  signedByUserId: number | null;
  signedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OnSiteChecklistResponse = {
  items: OnSiteChecklistItem[];
  signature: OnSiteChecklistSignature;
};

// Per-installation progress for the admin list view (checked steps / total).
export type OnSiteChecklistSummary = {
  installationId: number;
  done: number;
  total: number;
};

// Static per-step metadata not stored in the DB (title key + image filenames +
// optional warning key). Lookup table keyed by step number. Image paths are
// resolved at render time against the static assets folder.
export type OnSiteStepMeta = {
  number: number;
  titleKey: string;
  warnings?: OnSiteWarning[];
  images: string[];
};

// A single "Achtung" callout. severity 'error' renders red (used for the most
// critical warnings that appear in red in the source PDF).
export type OnSiteWarning = {
  key: string;
  severity: 'warning' | 'error';
};

export const ON_SITE_STEP_META: ReadonlyArray<OnSiteStepMeta> = [
  {
    number: 1,
    titleKey: 'onSiteStep1Title',
    warnings: [{ key: 'onSiteStep1Warning', severity: 'warning' }],
    images: ['step1-auspacken.jpg']
  },
  {
    number: 2,
    titleKey: 'onSiteStep2Title',
    images: ['step2-wr-montiert.jpg', 'step2-ac-anschluesse.jpg', 'step2-ac-stecker.jpg']
  },
  {
    number: 3,
    titleKey: 'onSiteStep3Title',
    warnings: [{ key: 'onSiteStep3Warning', severity: 'warning' }],
    images: ['step3-hauptverteilung.jpg', 'step3-hauptverteilung-2.jpg']
  },
  {
    number: 4,
    titleKey: 'onSiteStep4Title',
    warnings: [{ key: 'onSiteStep4Warning', severity: 'warning' }],
    images: ['step4-meter.jpg', 'step4-wandler.jpg']
  },
  {
    number: 5,
    titleKey: 'onSiteStep5Title',
    warnings: [{ key: 'onSiteStep5Warning', severity: 'warning' }],
    images: ['step5-gateway.jpg']
  },
  {
    number: 6,
    titleKey: 'onSiteStep6Title',
    warnings: [
      { key: 'onSiteStep6Warning1', severity: 'warning' },
      { key: 'onSiteStep6Warning2', severity: 'error' },
      { key: 'onSiteStep6Warning3', severity: 'warning' },
      { key: 'onSiteStep6Warning4', severity: 'warning' }
    ],
    images: ['step6-batterien.jpg', 'step6-splitter.jpg']
  },
  {
    number: 7,
    titleKey: 'onSiteStep7Title',
    warnings: [{ key: 'onSiteStep7Warning', severity: 'warning' }],
    images: ['step7-pv-switch.jpg']
  },
  {
    number: 8,
    titleKey: 'onSiteStep8Title',
    images: ['step8-batterie-breaker.jpg']
  }
];

export function parseOnSiteSubtasks(raw: string | null | undefined): OnSiteChecklistSubtask[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((x): OnSiteChecklistSubtask | null => {
        if (!x || typeof x !== 'object') return null;
        if (typeof x.heading === 'string') {
          return { kind: 'heading', heading: String(x.heading) };
        }
        if (typeof x.text === 'string') {
          return { kind: 'task', text: String(x.text), checked: Boolean(x.checked) };
        }
        return null;
      })
      .filter((x): x is OnSiteChecklistSubtask => x !== null);
  } catch {
    return [];
  }
}

export function serializeOnSiteSubtasks(subtasks: OnSiteChecklistSubtask[]): string {
  return JSON.stringify(
    subtasks.map((s) => {
      if (s.kind === 'heading') return { heading: s.heading };
      return { text: s.text, checked: s.checked };
    })
  );
}
